import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Link as LinkIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import Layout from "../components/layout/Layout";
import EmployeeList from "../components/employees/EmployeeList";
import EmployeeForm from "../components/employees/EmployeeForm";
import { useEmployees } from "../hooks/useEmployees";
import { useOrganization } from "../context/OrganizationContext";
import { useAuth } from "../context/AuthContext";
import { Employee } from "../types/employee.types";
import { invitationsService } from "../services/firebase/invitations.service";
import { employeesService } from "../services/firebase/employees.service";

export default function EmployeesInformationPage() {
  const { user } = useAuth();
  const { canWrite, organization, members, refreshMembers } = useOrganization();
  const {
    employees,
    isLoading,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    updateEmployeeStatus,
    isCreating,
    isUpdating,
    isDeleting,
  } = useEmployees();
  const { enqueueSnackbar } = useSnackbar();

  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [inviteSuccessOpen, setInviteSuccessOpen] = useState(false);

  // Calculate promoted employee IDs
  // An employee is promoted if they have a linkedUserId and that user is in the members collection
  // (members collection only includes staff with roles like admin/manager, not pure employees)
  const promotedEmployeeIds = useMemo(() => {
    if (!members) return [];
    const memberUserIds = new Set(members.map((m) => m.userId));
    return employees
      .filter((emp) => emp.linkedUserId && memberUserIds.has(emp.linkedUserId))
      .map((emp) => emp.id);
  }, [employees, members]);
  const [invitationLink, setInvitationLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );

  const handleCreateEmployee = async (data: {
    name: string;
    email?: string;
    phone?: string;
    assignedShopIds: string[];
  }) => {
    try {
      await createEmployee({
        name: data.name,
        email: data.email,
        phone: data.phone,
        assignedShopIds: data.assignedShopIds,
      });
      enqueueSnackbar("Employee created successfully!", { variant: "success" });
      setFormOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create employee";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleUpdateEmployee = async (data: {
    name: string;
    email?: string;
    phone?: string;
    assignedShopIds: string[];
  }) => {
    if (!selectedEmployee) return;

    try {
      await updateEmployee(selectedEmployee.id, data);
      enqueueSnackbar("Employee updated successfully!", { variant: "success" });
      setFormOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update employee";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      await deleteEmployee(selectedEmployee.id);
      enqueueSnackbar("Employee deleted successfully!", { variant: "success" });
      setDeleteDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete employee";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setFormOpen(true);
  };

  const handleDeleteClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDeleteDialogOpen(true);
  };

  const handleMarkInactiveClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setStatusDialogOpen(true);
  };

  const handleToggleStatus = async () => {
    if (!selectedEmployee) return;

    try {
      const newStatus =
        selectedEmployee.status === "active" ? "inactive" : "active";
      await updateEmployeeStatus(selectedEmployee.id, newStatus);
      enqueueSnackbar(
        `Employee marked as ${newStatus === "active" ? "active" : "inactive"}!`,
        { variant: "success" }
      );
      setStatusDialogOpen(false);
      setSelectedEmployee(null);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update employee status";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleAddEmployee = () => {
    setSelectedEmployee(null);
    setFormOpen(true);
  };

  const handleInviteEmployee = async (employee: Employee) => {
    if (!employee.email || !user?.organizationId || !organization) {
      enqueueSnackbar("Cannot create invitation - missing email or organization", {
        variant: "error",
      });
      return;
    }

    try {
      const result = await invitationsService.createEmployeeInvitation(
        user.organizationId,
        employee.id,
        employee.email,
        user.uid,
        {
          organizationName: organization.name,
          employeeName: employee.name,
        }
      );

      setInvitationLink(result.invitationLink);
      setInviteSuccessOpen(true);
      enqueueSnackbar("Invitation link created!", { variant: "success" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create invitation";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleResendInvite = async (employee: Employee) => {
    if (!employee.email || !user?.organizationId || !organization) {
      enqueueSnackbar("Cannot create invitation - missing email or organization", {
        variant: "error",
      });
      return;
    }

    try {
      // First, try to get the existing pending invitation
      const existingInvitation = await invitationsService.getPendingInvitationByEmployeeId(
        user.organizationId,
        employee.id
      );

      if (existingInvitation) {
        // Generate new token for existing invitation
        const result = await invitationsService.resendInvitation(
          user.organizationId,
          existingInvitation.id,
          {
            organizationName: organization.name,
            employeeName: employee.name,
          }
        );
        setInvitationLink(result.invitationLink);
      } else {
        // Create a new invitation if none exists
        const result = await invitationsService.createEmployeeInvitation(
          user.organizationId,
          employee.id,
          employee.email,
          user.uid,
          {
            organizationName: organization.name,
            employeeName: employee.name,
          }
        );
        setInvitationLink(result.invitationLink);
      }

      setInviteSuccessOpen(true);
      enqueueSnackbar("New invitation link created!", { variant: "success" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create invitation link";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      setLinkCopied(true);
      enqueueSnackbar("Link copied to clipboard!", { variant: "success" });
      setTimeout(() => setLinkCopied(false), 3000);
    } catch {
      enqueueSnackbar("Failed to copy link", { variant: "error" });
    }
  };

  const handleCloseInviteSuccess = () => {
    setInviteSuccessOpen(false);
    setInvitationLink("");
    setLinkCopied(false);
  };

  const handlePromoteEmployee = async (employee: Employee) => {
    if (!employee.linkedUserId || !user?.organizationId) {
      enqueueSnackbar("Cannot promote - employee has no linked account", {
        variant: "error",
      });
      return;
    }

    try {
      await employeesService.promoteEmployee(
        user.organizationId,
        employee.id,
        employee.linkedUserId,
        "manager" // Default promotion to manager role
      );
      refreshMembers();
      enqueueSnackbar(`${employee.name} has been promoted to Manager!`, {
        variant: "success",
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to promote employee";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleDemoteEmployee = async (employee: Employee) => {
    if (!employee.linkedUserId || !user?.organizationId) {
      enqueueSnackbar("Cannot demote - employee has no linked account", {
        variant: "error",
      });
      return;
    }

    try {
      await employeesService.demoteEmployee(
        user.organizationId,
        employee.id,
        employee.linkedUserId
      );
      refreshMembers();
      enqueueSnackbar(`${employee.name} has been demoted to Employee role`, {
        variant: "success",
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to demote employee";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  return (
    <Layout pageTitle="Employee Information">
      <Box>
        <EmployeeList
          employees={employees}
          isLoading={isLoading}
          canWrite={canWrite}
          onEdit={handleEditEmployee}
          onDelete={handleDeleteClick}
          onMarkInactive={handleMarkInactiveClick}
          onAddEmployee={handleAddEmployee}
          onInvite={handleInviteEmployee}
          onResendInvite={handleResendInvite}
          onPromote={handlePromoteEmployee}
          onDemote={handleDemoteEmployee}
          promotedEmployeeIds={promotedEmployeeIds}
        />

        {/* Employee Form Dialog */}
        <EmployeeForm
          open={formOpen}
          employee={selectedEmployee}
          onClose={() => {
            setFormOpen(false);
            setSelectedEmployee(null);
          }}
          onSubmit={
            selectedEmployee ? handleUpdateEmployee : handleCreateEmployee
          }
          isLoading={isCreating || isUpdating}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningIcon color="error" />
              Delete Employee
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>"{selectedEmployee?.name}"</strong>?
            </DialogContentText>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Consider using "Mark as Inactive" instead
              </Typography>
              <Typography variant="body2">
                Marking an employee as inactive keeps their data for records while hiding them from scheduling. You can reactivate them later if needed.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              If you delete this employee:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2, color: "text.secondary" }}>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Their employee record will be permanently removed
              </Typography>
              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                Historical schedules will still show their name
              </Typography>
              {selectedEmployee?.linkedUserId && (
                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                  Their user account will be removed from this organization
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            {selectedEmployee?.status === "active" && (
              <Button
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setStatusDialogOpen(true);
                }}
                color="warning"
                variant="outlined"
                disabled={isDeleting}
              >
                Mark as Inactive Instead
              </Button>
            )}
            <Button
              onClick={handleDeleteEmployee}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status Change Confirmation Dialog */}
        <Dialog
          open={statusDialogOpen}
          onClose={() => setStatusDialogOpen(false)}
        >
          <DialogTitle>
            {selectedEmployee?.status === "active"
              ? "Mark Employee as Inactive"
              : "Mark Employee as Active"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {selectedEmployee?.status === "active"
                ? `Are you sure you want to mark "${selectedEmployee?.name}" as inactive? They will no longer be available for scheduling.`
                : `Are you sure you want to mark "${selectedEmployee?.name}" as active? They will be available for scheduling again.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleToggleStatus}
              color={
                selectedEmployee?.status === "active" ? "warning" : "success"
              }
              variant="contained"
            >
              {selectedEmployee?.status === "active"
                ? "Mark as Inactive"
                : "Mark as Active"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invitation Link Dialog */}
        <Dialog
          open={inviteSuccessOpen}
          onClose={handleCloseInviteSuccess}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LinkIcon color="primary" />
              Invitation Link
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share this link with the employee to let them create their account:
            </Typography>
            <TextField
              fullWidth
              value={invitationLink}
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink} edge="end">
                      {linkCopied ? (
                        <CheckCircle color="success" />
                      ) : (
                        <ContentCopy />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <Alert severity="info">This invitation link expires in 7 days.</Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCopyLink}
              startIcon={linkCopied ? <CheckCircle /> : <ContentCopy />}
            >
              {linkCopied ? "Copied!" : "Copy Link"}
            </Button>
            <Button variant="contained" onClick={handleCloseInviteSuccess}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
